import { useAuth } from '@/contexts/AuthContext';
import { useJobAuthKey } from '@/hooks/useJobAuthKey';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from './ui/loading-spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ('staff' | 'client' | 'tutor')[];
  allowKey?: boolean;
}

export const ProtectedRoute = ({
  children,
  allowedRoles,
  allowKey = false,
}: ProtectedRouteProps) => {
  const { user, userRoles, userRolesLoading, sessionLoading } = useAuth();
  const key = useJobAuthKey();
  const isLoggedIn = !!user;

  // If auth is initialized and no user is found, redirect to login
  useEffect(() => {
    if (!sessionLoading && !isLoggedIn && !(allowKey && key)) {
      window.location.href = getRedirectUrl();
    }
  }, [sessionLoading, isLoggedIn, allowKey, key]);

  // Show loading state only if we're still initializing auth
  if (sessionLoading || userRolesLoading || (!user && !(allowKey && key))) {
    return <LoadingSpinner />;
  }

  // If user doesn't have allowed role, redirect to appropriate dashboard
  if (allowedRoles && userRoles && !allowedRoles.some((role) => userRoles.includes(role))) {
    console.log(`User role ${userRoles} not allowed, redirecting`);

    // Redirect to appropriate dashboard based on role
    if (userRoles.includes('staff')) {
      return <Navigate to="/staff/jobs" replace />;
    }
    if (userRoles.includes('client')) {
      return <Navigate to="/client/reports" replace />;
    }
    if (userRoles.includes('tutor')) {
      return <Navigate to="/tutor/jobs-board" replace />;
    }
  }

  return <>{children}</>;
};

const getRedirectUrl = () => {
  const decoded = location.pathname + location.search;
  if (decoded === '/') {
    return '/auth';
  }
  return `/auth?redirect_to=${encodeURIComponent(decoded)}`;
};
